"use client";

import { useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  CrosstabTables,
  PERCENT_KINDS,
} from "@/components/tools/stats-descriptive-more/crosstab-views";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  buildCrosstab,
  type CategoryOrder,
  type ParsedPairs,
  type PercentKind,
  parseLists,
  parsePairs,
} from "@/lib/tool-kit/stats-descriptive-more/crosstab";

/** English strings. A translated content file overrides these through `ui`. */
export const CROSSTAB_GENERATOR_STRINGS = {
  entryLabel: "Data entry",
  entryPairs: "Two columns pasted together",
  entryLists: "Two separate lists",
  pairsLabel:
    "Your data — one case per line, two values separated by a tab or comma",
  pairsPlaceholder: "First-year, Library\nSenior, Home\nSenior, Cafe",
  listALabel: "Row variable — one value per line",
  listAPlaceholder: "First-year\nSenior\nSenior",
  listBLabel: "Column variable — one value per line",
  listBPlaceholder: "Library\nHome\nCafe",
  casesRead: "one: case read|other: cases read",
  droppedHint:
    "one: , {count} incomplete case left out|other: , {count} incomplete cases left out",
  header: "The first line holds the variable names",
  ignoreCase: "Treat upper and lower case as the same",
  orderLabel: "Order of categories",
  orderFirst: "As they first appear",
  orderAlphabetical: "Alphabetical",
  percentLabel: "Percentages in the APA table",
  percentRow: "Row %",
  percentColumn: "Column %",
  percentTotal: "Total %",
  rowNameLabel: "Row variable name",
  rowNamePlaceholder: "Class standing",
  colNameLabel: "Column variable name",
  colNamePlaceholder: "Study spot",
  numberLabel: "Table number",
  tryExample: "Try an example",
  clear: "Clear",
  enterPrompt:
    "Enter some cases — each with a value for both variables — to build the table.",
  mismatch:
    "The two lists differ in length (row variable: {a}, column variable: {b}). Each case needs one value in each list.",
  unreadable:
    "one: {count} line did not have two values and was skipped.|other: {count} lines did not have two values and were skipped.",
  tooMany:
    "A variable has more than 25 categories — a crosstab that wide is not readable. Group the categories first.",
  results: "Crosstab",
  defaultRowName: "Row variable",
  defaultColName: "Column variable",
  total: "Total",
  titleCounts: "Counts",
  titleRowPercent: "Row percentages (each row adds to 100%)",
  titleColumnPercent: "Column percentages (each column adds to 100%)",
  titleTotalPercent: "Total percentages (the table adds to 100%)",
  apaHeading: "APA-style table",
  apaTable: "Table",
  apaNote: "Note",
  apaTitle: "{row} by {col}",
  noteRow: "Cell entries are _n_ (row percentage).",
  noteColumn: "Cell entries are _n_ (column percentage).",
  noteTotal: "Cell entries are _n_ (percentage of all cases).",
  copyApa: "Copy APA table",
  copyCsv: "Copy counts as CSV",
  copyMarkdown: "Copy counts as Markdown",
  copyPercentCsv: "Copy percentages as CSV",
  copied: "Copied!",
  sizeLine: "{rows} × {cols} table from {n} cases",
  exampleData:
    "Class standing, Study spot\nFirst-year, Library\nFirst-year, Library\nFirst-year, Library\nFirst-year, Library\nFirst-year, Library\nFirst-year, Library\nFirst-year, Library\nFirst-year, Home\nFirst-year, Home\nFirst-year, Home\nFirst-year, Home\nFirst-year, Cafe\nFirst-year, Cafe\nFirst-year, Cafe\nSenior, Library\nSenior, Library\nSenior, Library\nSenior, Library\nSenior, Library\nSenior, Home\nSenior, Home\nSenior, Home\nSenior, Home\nSenior, Home\nSenior, Home\nSenior, Home\nSenior, Home\nSenior, Home\nSenior, Cafe\nSenior, Cafe\nSenior, Cafe\nSenior, Cafe\nSenior, Cafe\nSenior, Cafe\nSenior, Cafe\nSenior, Cafe",
} as const;

type Entry = "pairs" | "lists";

function droppedCount(parsed: ParsedPairs): number {
  return parsed.missing + parsed.unreadable;
}

export default function CrosstabGeneratorWidget() {
  const strings = useToolUi(CROSSTAB_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [entry, setEntry] = useState<Entry>("pairs");
  const [pairsText, setPairsText] = useState("");
  const [listA, setListA] = useState("");
  const [listB, setListB] = useState("");
  const [header, setHeader] = useState(false);
  const [ignoreCase, setIgnoreCase] = useState(true);
  const [order, setOrder] = useState<CategoryOrder>("first");
  const [kind, setKind] = useState<PercentKind>("row");
  const [rowName, setRowName] = useState("");
  const [colName, setColName] = useState("");
  const [tableNumber, setTableNumber] = useState("1");

  const parsed: ParsedPairs =
    entry === "pairs"
      ? parsePairs(pairsText, header)
      : parseLists(listA, listB, header);
  const { mismatch } = parsed;
  const ct = mismatch
    ? null
    : buildCrosstab(parsed.pairs, { ignoreCase, order });
  const table = ct !== null && ct !== "tooMany" ? ct : null;
  const names = {
    row: rowName.trim() || parsed.rowName || strings.defaultRowName,
    col: colName.trim() || parsed.colName || strings.defaultColName,
    number: tableNumber,
  };
  const view = { strings, fmt };
  const dropped = droppedCount(parsed);
  const hasInput = pairsText !== "" || listA !== "" || listB !== "";

  return (
    <>
      <section className="space-y-4">
        <PillGroup
          label={strings.entryLabel}
          name="cg-entry"
          onChange={setEntry}
          options={[
            { value: "pairs", label: strings.entryPairs },
            { value: "lists", label: strings.entryLists },
          ]}
          value={entry}
        />
        {entry === "pairs" ? (
          <TextAreaField
            hint={
              <>
                {fmt(parsed.pairs.length)}
                {tPlural(strings.casesRead, locale, parsed.pairs.length)}
                {dropped > 0
                  ? tPlural(strings.droppedHint, locale, dropped, {
                      count: fmt(dropped),
                    })
                  : null}
              </>
            }
            id="cg-pairs"
            label={strings.pairsLabel}
            onChange={setPairsText}
            placeholder={strings.pairsPlaceholder}
            value={pairsText}
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextAreaField
              id="cg-a"
              label={strings.listALabel}
              onChange={setListA}
              placeholder={strings.listAPlaceholder}
              value={listA}
            />
            <TextAreaField
              id="cg-b"
              label={strings.listBLabel}
              onChange={setListB}
              placeholder={strings.listBPlaceholder}
              value={listB}
            />
          </div>
        )}
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={header}
            label={strings.header}
            onChange={setHeader}
          />
          <Checkbox
            checked={ignoreCase}
            label={strings.ignoreCase}
            onChange={setIgnoreCase}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            id="cg-row-name"
            label={strings.rowNameLabel}
            onChange={setRowName}
            placeholder={strings.rowNamePlaceholder}
            value={rowName}
          />
          <TextField
            id="cg-col-name"
            label={strings.colNameLabel}
            onChange={setColName}
            placeholder={strings.colNamePlaceholder}
            value={colName}
          />
          <TextField
            id="cg-number"
            label={strings.numberLabel}
            onChange={setTableNumber}
            value={tableNumber}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="cg-order"
            label={strings.orderLabel}
            onChange={setOrder}
            options={[
              { value: "first", label: strings.orderFirst },
              { value: "alphabetical", label: strings.orderAlphabetical },
            ]}
            value={order}
          />
          <PillGroup
            label={strings.percentLabel}
            name="cg-percent"
            onChange={setKind}
            options={PERCENT_KINDS.map((value) => ({
              value,
              label: {
                row: strings.percentRow,
                column: strings.percentColumn,
                total: strings.percentTotal,
              }[value],
            }))}
            value={kind}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setEntry("pairs");
              setPairsText(strings.exampleData);
              setHeader(true);
            }}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={!hasInput}
            onClick={() => {
              setPairsText("");
              setListA("");
              setListB("");
            }}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {mismatch ? (
        <ErrorNotice>
          {t(strings.mismatch, { a: fmt(mismatch[0]), b: fmt(mismatch[1]) })}
        </ErrorNotice>
      ) : null}
      {ct === "tooMany" ? <ErrorNotice>{strings.tooMany}</ErrorNotice> : null}
      {parsed.unreadable > 0 ? (
        <Notice>
          {tPlural(strings.unreadable, locale, parsed.unreadable, {
            count: fmt(parsed.unreadable),
          })}
        </Notice>
      ) : null}
      {table ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <p aria-live="polite" className="text-muted-foreground text-sm">
            {t(strings.sizeLine, {
              rows: fmt(table.rowLabels.length),
              cols: fmt(table.colLabels.length),
              n: fmt(table.total),
            })}
          </p>
          <CrosstabTables ct={table} kind={kind} names={names} view={view} />
        </section>
      ) : null}
      {table || mismatch || ct === "tooMany" ? null : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </>
  );
}
